import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { registerFormStyles, createDynamicStyles } from '../styles/components/RegisterForm.styles';
import styles, { hoverEffects, styleUtils } from '../styles/components/CreditCardEditForm.styles';
import { getEnvironmentConfig, ENDPOINTS } from '../config/apiEndpoints';
import { PREFECTURES, getPrefectureCode } from '../data/prefectures';
import { 
  formatCardNumber, 
  formatCVV, 
  formatCardholderName,
  formatZipCode 
} from '../utils/creditCardFormatters';
import { 
  validateCardNumber, 
  validateExpiry, 
  validateCvv, 
  validateCardholderName,
  validateBillingZipCode,
  validateBillingCity,
  validateBillingAddress,
  validateBillingState
} from '../utils/creditCardValidators';
import {
  generatePaygentToken,
  checkPaygentTokenLoaded,
  display3DSScreen
} from '../utils/paygentTokenHelper';
import { detectCardBrand } from '../utils/cardBrandDetector';
import PaymentProcessing from '../components/ui/PaymentProcessing';
import { PersonalInfoConsent, BUTTON_TEXT, ERROR_TRANSLATIONS } from '../data/creditCardData.js';
import { formatPostalCode } from '../utils/formValidation';
import useAddressSearch from '../hooks/useAddressSearch';
import PostalCodeInput from '../components/ui/PostalCodeInput';


/**
 * エラーメッセージを日本語化
 * @param {string} errorMessage - エラーメッセージ
 * @returns {string} 翻訳されたエラーメッセージ
 */
const translateErrorMessage = (errorMessage) => {
  for (const [key, translation] of Object.entries(ERROR_TRANSLATIONS)) {
    if (errorMessage.includes(key)) {
      return translation;
    }
  }
  return errorMessage;
};

/**
 * クレジットカード変更ページコンポーネント
 */
const CreditCardEditPage = ({ onSuccess }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  const { currentUser, getCsrfTokenFromCookie, isAuthenticated } = useAuth();

  // 状態管理
  const [customerId, setCustomerId] = useState(null);
  const [memberInfo, setMemberInfo] = useState({
    loading: true,
    data: null,
    error: null
  });
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    billingZipCode: '',
    billingCity: '',
    billingAddress: '',
    billingState: '',
    useMemberAddress: true // デフォルトで会員住所を使用
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  /**
   * 決済処理中画面の状態管理
   */
  const [processingState, setProcessingState] = useState({
    isProcessing: false,
    cardBrand: 'UNKNOWN',
    message: '決済処理中...'
  });

  /**
   * 会員情報を取得する関数
   */
  const fetchMemberInfo = async () => {
    if (!isAuthenticated()) {
      console.error('[CREDIT CARD EDIT] 認証されていません');
      setMemberInfo({
        loading: false,
        data: null,
        error: '認証されていません'
      });
      return;
    }

    try {
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('CSRFトークンの取得に失敗しました');
      }

      const config = getEnvironmentConfig();
      const apiUrl = `${config.baseUrl}${ENDPOINTS.MEMBER.ME}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('会員情報の取得に失敗しました');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || '会員情報の取得に失敗しました');
      }

      const memberData = data.member || data.user || data;
      const fetchedCustomerId = memberData.customerId || memberData.memberId || memberData.id;
      
      if (!fetchedCustomerId) {
        throw new Error('顧客IDが取得できませんでした');
      }

      setCustomerId(fetchedCustomerId);
      setMemberInfo({
        loading: false,
        data: memberData,
        error: null
      });
      
    } catch (error) {
      console.error('[CREDIT CARD EDIT] 会員情報取得エラー:', error);
      setMemberInfo({
        loading: false,
        data: null,
        error: error.message
      });
      showError('会員情報の取得に失敗しました');
    } finally {
      setIsInitializing(false);
    }
  };

  /**
   * 初期化処理
   */
  useEffect(() => {
    fetchMemberInfo();
    // マウント時に一度だけ取得する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 会員情報取得後、住所が未設定の場合はチェックを自動で外す
   */
  useEffect(() => {
    if (!memberInfo.data) return;

    const hasMemberAddress =
      memberInfo.data.postalCode &&
      memberInfo.data.prefecture &&
      memberInfo.data.city &&
      memberInfo.data.address;

    if (!hasMemberAddress) {
      setFormData(prev => ({ ...prev, useMemberAddress: false }));
    }
  }, [memberInfo.data]);

  const handleCompositionStart = () => {
  setIsComposing(true);
};

  /**
   * IMEハンドラー
   */
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    const { name, value } = e.target;
    
    const formatters = {
      cardNumber: formatCardNumber,
      cvv: formatCVV,
      cardholderName: formatCardholderName,
      billingZipCode: formatZipCode
    };
    
    const formattedValue = formatters[name] ? formatters[name](value) : value;
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  /**
   * 入力フィールド変更ハンドラー
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));

      // useMemberAddressがtrueになった時、請求先住所のエラーをクリア
      if (name === 'useMemberAddress' && checked) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.billingZipCode;
          delete newErrors.billingState;
          delete newErrors.billingCity;
          delete newErrors.billingAddress;
          return newErrors;
        });
      }
      return;
    }

    // IME変換中はフォーマットをスキップ
    if (isComposing) {
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    const formatters = {
      cardNumber: formatCardNumber,
      cvv: formatCVV,
      cardholderName: formatCardholderName,
      billingZipCode: formatZipCode
    };
    
    const formattedValue = formatters[name] ? formatters[name](value) : value;
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  /**
   * フォームバリデーション
   */
  const validateForm = (formData) => {
    const errors = {};
    
    // カード情報のバリデーション
    const cardNumberError = validateCardNumber(formData.cardNumber);
    if (cardNumberError) errors.cardNumber = cardNumberError;
    
    const expiryError = validateExpiry(formData.expiryMonth, formData.expiryYear);
    if (expiryError) errors.expiry = expiryError;
    
    const cvvError = validateCvv(formData.cvv);
    if (cvvError) errors.cvv = cvvError;
    
    const cardholderNameError = validateCardholderName(formData.cardholderName);
    if (cardholderNameError) errors.cardholderName = cardholderNameError;
    
    // 会員住所を使用する場合は請求先住所のバリデーションをスキップ
    if (formData.useMemberAddress) {
      return errors;
    }

    // 請求先住所のバリデーション
    const billingZipCodeError = validateBillingZipCode(formData.billingZipCode);
    if (billingZipCodeError) errors.billingZipCode = billingZipCodeError;
    
    const billingStateError = validateBillingState(formData.billingState);
    if (billingStateError) errors.billingState = billingStateError;
    
    const billingCityError = validateBillingCity(formData.billingCity);
    if (billingCityError) errors.billingCity = billingCityError;
    
    const billingAddressError = validateBillingAddress(formData.billingAddress);
    if (billingAddressError) errors.billingAddress = billingAddressError;

    return errors;
  };

  /**
   * 新規カード登録処理（カード更新用エンドポイント使用）
   */
  const updateCardWithBackend = async (token) => {
    showInfo('カード情報を更新中...', 3000);

    const csrfToken = await getCsrfTokenFromCookie(true);
    if (!csrfToken) {
      throw new Error('CSRFトークンの取得に失敗しました');
    }

    const config = getEnvironmentConfig();
    // カード更新専用のエンドポイントを使用
    const apiUrl = `${config.baseUrl}${ENDPOINTS.CREDIT_CARD.CARD_CHANGE}`;

    // 会員住所を使用する場合は会員情報から取得
    const billingData = formData.useMemberAddress 
      ? {
          bill_address_city: memberInfo.data?.city || '',
          bill_address_line1: memberInfo.data?.address || '',
          bill_address_post_code: (memberInfo.data?.postalCode || '').replace(/-/g, ''),
          bill_address_state: getPrefectureCode(memberInfo.data?.prefecture) || '',
        }
      : {
          bill_address_city: formData.billingCity,
          bill_address_line1: formData.billingAddress,
          bill_address_post_code: formData.billingZipCode.replace(/-/g, ''),
          bill_address_state: formData.billingState,
        };

    const requestData = {
      customerId: customerId,
      token: token,
      cardholder_name: formData.cardholderName,
      email_address: currentUser?.email || memberInfo.data?.email || '',
      ...billingData,
      bill_address_country: '392', // 日本
      execute_initial_payment: false // カード変更時は初期決済を実行しない
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken
      },
      body: JSON.stringify(requestData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'カード更新に失敗しました');
    }

    return data;
  };

  /**
   * フォーム送信処理
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 会員情報チェック
    if (memberInfo.loading) {
      showError('会員情報を取得中です。しばらくお待ちください。', 3000);
      return;
    }

    if (memberInfo.error || !memberInfo.data) {
      showError('会員情報が取得できていません。ページを再読み込みしてください。', 5000);
      return;
    }

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showError('入力内容を確認してください', 5000);
      return;
    }

    if (!customerId) {
      showError('顧客IDが取得できていません。ページを再読み込みしてください。');
      return;
    }
    
    // カードブランドを判定
    const cardBrand = detectCardBrand(formData.cardNumber);

    // 処理中画面を表示
    setProcessingState({
      isProcessing: true,
      cardBrand: cardBrand,
      message: 'カード情報を処理中...'
    });

    setIsSubmitting(true);
    
    try {
      // STEP 1: Paygentトークンを生成
      setProcessingState(prev => ({
        ...prev,
        message: 'カード情報を処理中...'
      }));
      
      const tokenResult = await generatePaygentToken(formData);

      // STEP 2: カード情報を更新
      setProcessingState(prev => ({
        ...prev,
        message: 'カード情報を更新中...'
      }));
      const result = await updateCardWithBackend(tokenResult.token);
      
      // 3DS認証が必要な場合
      if (result.redirect_html || result.redirectHtml) {
        setProcessingState(prev => ({
          ...prev,
          message: '本人認証画面に移動します...'
        }));

        const redirectHtml = result.redirect_html || result.redirectHtml;
        
        // 少し待ってから3DS画面を表示
        setTimeout(() => {
          display3DSScreen(redirectHtml);
        }, 1000);
        // この時点でフロントエンド側の処理は完全に終了
        return;
      }
      
      // 3DS認証不要の場合（カード更新が即座に完了）
      setProcessingState(prev => ({
        ...prev,
        message: '登録完了しました'
      }));

      showSuccess('クレジットカード情報を更新しました');
      
      if (onSuccess) {
        onSuccess();
      } else {
        setTimeout(() => navigate(ROUTES.HOME), 1500);
      }
      
    } catch (error) {
      console.error('[CREDIT CARD EDIT] クレジットカード更新エラー:', error);
      
      // エラー時は処理中画面を非表示
      setProcessingState({
        isProcessing: false,
        cardBrand: 'UNKNOWN',
        message: ''
      });

      if (error.message.includes('トークン生成') || error.message.includes('PaygentToken')) {
        showError('カード情報の処理に失敗しました。入力内容を確認してください。', 8000);
      } else {
        // エラーメッセージを翻訳
        const translatedMessage = translateErrorMessage(error.message);
        showError(translatedMessage || `予期しないエラーが発生しました: ${error.message}`, 8000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { postalMessage, isSuccess, searchAddress: searchBillingAddress } = useAddressSearch(({ prefectureCode, city, address }) => {
    setFormData(prev => ({
      ...prev,
      billingState: prefectureCode,
      billingCity: city,
      billingAddress: address
    }));
  });

  /**
   * 戻るボタン処理
   */
  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  /**
   * 入力フィールド行の生成
   */
  const renderInputField = (name, label, required = true, props = {}) => {
    const { helpText, ...inputProps } = props;

    return (
      <tr key={name} className="credit-card-table-row">
        <th style={registerFormStyles.th} className="credit-card-table-th">
          <span>{label}</span>{required && <span style={registerFormStyles.required}> *</span>}
        </th>
        <td style={registerFormStyles.td} className="credit-card-table-td">
          <input
            name={name}
            value={formData[name]}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}      
            style={createDynamicStyles.getInputStyle(!!validationErrors[name])}
            required={required}
            disabled={isSubmitting}
            {...inputProps}
          />
          {validationErrors[name] && (
            <div style={createDynamicStyles.getMessageStyle('error')}>
              {validationErrors[name]}
            </div>
          )}
          {helpText && <p style={registerFormStyles.smallText}>{helpText}</p>}
        </td>
      </tr>
    );
  };

  /**
   * セレクトフィールドの生成
   */
  const renderSelectField = (name, options, errorField = null) => (
    <select
      name={name}
      value={formData[name]}
      onChange={handleChange}
      style={{ 
        ...registerFormStyles.select, 
        width: name === 'billingState' ? '100%' : '100px',
        maxWidth: name === 'billingState' ? '300px' : '100px',
        ...(validationErrors[errorField || name] && { 
          borderWidth: '1px', 
          borderStyle: 'solid', 
          borderColor: '#dc3545' 
        })
      }}
      required 
      disabled={isSubmitting}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );

  /**
   * 年月選択オプション生成
   */
  const getMonthOptions = () => [
    { value: '', label: '月' },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      label: `${String(i + 1).padStart(2, '0')}月`
    }))
  ];

  const getYearOptions = () => [
    { value: '', label: '年' },
    ...Array.from({ length: 10 }, (_, i) => {
      const year = new Date().getFullYear() + i;
      return { 
        value: String(year).slice(-2), 
        label: `${year}年` 
      };
    })
  ];

  if (isInitializing || memberInfo.loading) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  if (memberInfo.error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <h2>エラーが発生しました</h2>
          <p>{memberInfo.error}</p>
          <button onClick={() => navigate(ROUTES.HOME)}>ホームに戻る</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="credit-card-container">
      <div style={styles.content} className="credit-card-content">
        <div style={styles.header} className="credit-card-header">
          <h1 style={styles.title} className="credit-card-title">クレジットカード変更</h1>
          <button
            type="button"
            style={styles.backButton}
            className="credit-card-back-button"
            onMouseOver={(e) => hoverEffects.applyBackButtonHover(e.target)}
            onMouseOut={(e) => hoverEffects.resetBackButtonHover(e.target)}
            onClick={handleBack}
            disabled={isSubmitting}
          >
            ← ホームに戻る
          </button>
        </div>
        
        <div style={styles.notice} className="credit-card-notice">
          <strong>⚠️重要</strong><br/>
          <span>当サイトは3Dセキュア (本人認証サービス)を導入しています。</span><br/>
          <span>本人認証時にワンタイムパスワード等の入力を求められた場合は、画面の案内に沿って手続きを進めてください。</span><br/>
          <span>クレジットカード情報は安全に暗号化されて送信されます。
          CVVは保存されず、認証のためにのみ使用されます。</span>
        </div>
        
        <form onSubmit={handleSubmit} noValidate>
          <h2 style={registerFormStyles.pageTitle}>クレジットカード情報変更</h2>
          
          <table style={registerFormStyles.table} className="credit-card-table">
            <tbody>
              {renderInputField('cardNumber', 'カード番号', true, {
                type: 'text', 
                placeholder: '1234 5678 9012 3456', 
                maxLength: '19'
              })}
              
              {renderInputField('cardholderName', 'カード名義', true, {
                type: 'text', 
                placeholder: 'TARO YAMADA', 
                maxLength: '26',
                helpText: 'カードに記載されている名前をローマ字で入力してください'
              })}

              <tr className="credit-card-table-row">
                <th style={registerFormStyles.th} className="credit-card-table-th">
                  <span>有効期限</span><span style={registerFormStyles.required}> *</span>
                </th>
                <td style={registerFormStyles.td} className="credit-card-table-td">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }} className="credit-card-expiry-row">
                    {renderSelectField('expiryMonth', getMonthOptions(), 'expiry')}
                    <span>/</span>
                    {renderSelectField('expiryYear', getYearOptions(), 'expiry')}
                  </div>
                  {validationErrors.expiry && (
                    <div style={createDynamicStyles.getMessageStyle('error')}>
                      {validationErrors.expiry}
                    </div>
                  )}
                </td>
              </tr>

              {renderInputField('cvv', 'セキュリティコード', true, {
                type: 'text', 
                placeholder: '123', 
                maxLength: '4', 
                style: { 
                  ...createDynamicStyles.getInputStyle(!!validationErrors.cvv), 
                  width: '80px' 
                },
                helpText: 'カード裏面の3桁または4桁の番号'
              })}

              {/* 会員住所使用チェックボックス */}
              <tr>
                <th style={registerFormStyles.th} className="credit-card-table-th">請求先住所</th>
                <td style={registerFormStyles.td} className="credit-card-table-td">
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    padding: '10px 0',
                    flexWrap: 'wrap'
                  }}>
                    <input
                      type="checkbox"
                      name="useMemberAddress"
                      checked={formData.useMemberAddress}
                      onChange={handleChange}
                      disabled={isSubmitting || !memberInfo.data?.postalCode || !memberInfo.data?.prefecture || !memberInfo.data?.city || !memberInfo.data?.address}
                      style={{
                        width: '18px',
                        height: '18px',
                        flexShrink: 0,
                        cursor: (!memberInfo.data?.postalCode || !memberInfo.data?.prefecture || !memberInfo.data?.city || !memberInfo.data?.address) ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                      会員登録情報と同じ住所を使用する
                    </span>
                  </label>
                  {(!memberInfo.data?.postalCode || !memberInfo.data?.prefecture || !memberInfo.data?.city || !memberInfo.data?.address) && (
                    <div style={{ fontSize: '13px', color: '#dc3545', marginTop: '4px' }}>
                      会員情報に住所が登録されていないため選択できません。請求先住所を直接入力してください。
                    </div>
                  )}
                  {memberInfo.data && formData.useMemberAddress && (
                    <div style={{
                      marginTop: '10px',
                      padding: '12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#666',
                      lineHeight: '1.6',
                      wordBreak: 'break-all'
                    }} translate="no">
                      <div style={{ marginBottom: '4px' }}>{`〒${memberInfo.data.postalCode ?? ''}`}</div>
                      <div style={{ marginBottom: '4px' }}>{`${memberInfo.data.prefecture ?? ''} ${memberInfo.data.city ?? ''}`}</div>
                      <div style={{ marginBottom: '4px' }}>{memberInfo.data.address}</div>
                      {memberInfo.data.buildingRoom && <div>{memberInfo.data.buildingRoom}</div>}
                    </div>
                  )}
                </td>
              </tr>

              {/* 請求先住所入力フィールド(useMemberAddress=falseの時のみ表示) */}
              {!formData.useMemberAddress && (
                <>
                  {/* 請求先郵便番号 - 住所検索機能付き */}
                  <PostalCodeInput
                    label="請求先郵便番号"
                    required
                    name="billingZipCode"
                    value={formData.billingZipCode}
                    onChange={(e) => {
                      const formatted = formatPostalCode(e.target.value);
                      setFormData(prev => ({ ...prev, billingZipCode: formatted }));
                      if (validationErrors.billingZipCode) {
                        setValidationErrors(prev => ({ ...prev, billingZipCode: undefined }));
                      }
                    }}
                    onSearch={searchBillingAddress}
                    error={validationErrors.billingZipCode}
                    postalMessage={postalMessage}
                    isSuccess={isSuccess}
                    disabled={isSubmitting}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    thStyle={registerFormStyles.th}
                    tdStyle={registerFormStyles.td}
                    inputStyle={createDynamicStyles.getInputStyle(!!validationErrors.billingZipCode)}
                    errorStyle={createDynamicStyles.getMessageStyle('error')}
                    helpText="カードの請求書が送付される住所の郵便番号を入力してください"
                    helpTextStyle={registerFormStyles.smallText}
                    requiredStyle={registerFormStyles.required}
                    rowClassName="credit-card-table-row"
                    thClassName="credit-card-table-th"
                    tdClassName="credit-card-table-td"
                  />

                  <tr>
                    <th style={registerFormStyles.th} className="credit-card-table-th">
                      <span>都道府県</span><span style={registerFormStyles.required}> *</span>
                    </th>
                    <td style={registerFormStyles.td} className="credit-card-table-td">
                      {renderSelectField('billingState', PREFECTURES)}
                      {validationErrors.billingState && (
                        <div style={createDynamicStyles.getMessageStyle('error')}>
                          {validationErrors.billingState}
                        </div>
                      )}
                    </td>
                  </tr>

                  {renderInputField('billingCity', '市区町村', true, {
                    type: 'text', 
                    placeholder: '渋谷区'
                  })}

                  {renderInputField('billingAddress', '住所', true, {
                    type: 'text', 
                    placeholder: '渋谷1-2-3',
                    helpText: '番地・建物名を入力してください'
                  })}
                </>
              )}
            </tbody>
          </table>

          <PersonalInfoConsent />
          <div style={{ 
            ...styles.buttonContainer,
            flexWrap: 'wrap',
            gap: '10px'
          }} className="credit-card-button-container">
            <button
              type="button"
              style={{
                ...styleUtils.getButtonStyle('secondary'),
                minWidth: '120px',
                flex: '1 1 auto'
              }}
              className="credit-card-button"
              onMouseOver={(e) => hoverEffects.applySecondaryButtonHover(e.target)}
              onMouseOut={(e) => hoverEffects.resetSecondaryButtonHover(e.target)}
              onClick={handleBack}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              style={{
                ...styleUtils.getButtonStyle('primary', isSubmitting),
                backgroundColor: '#09529f',
                minWidth: '120px',
                flex: '1 1 auto'
              }}
              className="credit-card-button"
              onMouseOver={(e) => hoverEffects.applyPrimaryButtonHover(e.target, isSubmitting)}
              onMouseOut={(e) => hoverEffects.resetPrimaryButtonHover(e.target, isSubmitting)}
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? BUTTON_TEXT.update.submitting 
                : BUTTON_TEXT.update.submit
              }
            </button>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div style={{
              marginTop: '20px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <strong>デバッグ情報:</strong>
              <br /><span>{`Customer ID: ${customerId || 'なし'}`}</span>
              <br /><span>{`PaygentToken: ${checkPaygentTokenLoaded() ? 'ロード済み' : '未ロード'}`}</span>
              <br /><span>{`送信中: ${isSubmitting ? 'はい' : 'いいえ'}`}</span>
              <br /><span>3DS処理: バックエンド自動処理</span>
              <br /><span>{`会員住所使用: ${formData.useMemberAddress ? 'はい' : 'いいえ'}`}</span>
            </div>
          )}
        </form>
      </div>

      {/* 処理中画面 */}
      {processingState.isProcessing && (
        <PaymentProcessing
          cardBrand={processingState.cardBrand}
          message={processingState.message}
        />
      )}
    </div>
  );
};

export default CreditCardEditPage;